import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { Button } from '../button';
import { ButtonGroup } from '../button-group';
import { CALENDAR_CHANGE_REASON } from '../calendar/constants/calendar-change-reason';
import { DatePickerRootContext } from './context/date-picker-root.context';
import { datePickerButtonGroupStyles } from './styles/date-picker-button-group.styles';
import type { DatePickerButtonGroupPropsInterface } from './types/date-picker-button-group.types';

/**
 * @since 10.0.0
 */
export const DatePickerButtonGroup = forwardRef<
  HTMLDivElement,
  DatePickerButtonGroupPropsInterface
>((props: DatePickerButtonGroupPropsInterface, forwardedRef) => {
  const {
    className,
    css: cssProp,
    onClickCancel,
    onClickOk,
    onKeyDownCancel,
    onKeyDownOk,
    ...rest
  } = props;

  const styles = css(datePickerButtonGroupStyles, cssProp);

  const { datePicker } = useContext(DatePickerRootContext);

  const handleCancel = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (datePicker.value !== undefined && datePicker.onValueChange) {
        datePicker.resetPendingValue?.();
        datePicker.onValueChange(datePicker.value, { reason: CALENDAR_CHANGE_REASON.cancel });
      }

      onClickCancel?.(event);
    },
    [datePicker, onClickCancel],
  );

  const handleOk = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (datePicker.pendingValue !== undefined && datePicker.onValueChange) {
        datePicker.onValueChange(datePicker.pendingValue, { reason: CALENDAR_CHANGE_REASON.ok });
      }

      onClickOk?.(event);
    },
    [datePicker, onClickOk],
  );

  const handleCancelKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (event) => {
      if (datePicker.value !== undefined && datePicker.onValueChange && event.key === 'Enter') {
        datePicker.resetPendingValue?.();
        datePicker.onValueChange(datePicker.value, { reason: CALENDAR_CHANGE_REASON.okKeyboard });
      }

      onKeyDownCancel?.(event);
    },
    [datePicker, onKeyDownCancel],
  );

  const handleOkKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (event) => {
      if (
        datePicker.pendingValue !== undefined &&
        datePicker.onValueChange &&
        event.key === 'Enter'
      ) {
        datePicker.onValueChange(datePicker.pendingValue, {
          reason: CALENDAR_CHANGE_REASON.okKeyboard,
        });
      }

      onKeyDownOk?.(event);
    },
    [datePicker, onKeyDownOk],
  );

  if (datePicker.closeModalOnDateSelect) {
    return null;
  }

  return (
    <div
      ref={forwardedRef}
      data-c11n-version={__C11N_VERSION__}
      data-c11n-component="DatePicker.ButtonGroup"
      className={cx('c11n', styles, className)}
      {...rest}
    >
      <ButtonGroup aria-label="Date picker buttons to close the calendar">
        <Button tone="brand" emphasis="bare" onClick={handleCancel} onKeyDown={handleCancelKeyDown}>
          Cancel
        </Button>
        <Button tone="brand" emphasis="filled" onClick={handleOk} onKeyDown={handleOkKeyDown}>
          Select
        </Button>
      </ButtonGroup>
    </div>
  );
});

DatePickerButtonGroup.displayName = 'DatePicker.ButtonGroup';
